import { useEffect, useRef, useState } from "react";

export interface AudioRecorderState {
  isRecording: boolean;
  recordingDuration: number;
  audioBlob: Blob | null;
  isProcessing: boolean;
}

export interface AudioRecorderControls {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
  resetRecording: () => void;
}

export interface UseAudioRecorderOptions {
  maxDuration?: number;
  mimeType?: string;
  timeslice?: number;
  audioConstraints?: MediaTrackConstraints;
}

export function useAudioRecorder({
  maxDuration = 60,
  timeslice = 250,
  audioConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 48000,
  },
}: UseAudioRecorderOptions = {}): [AudioRecorderState, AudioRecorderControls] {
  // State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingTimerRef = useRef<number | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Get supported MIME type for current browser
  const getSupportedMimeType = () => {
    const types = [
      "audio/webm",
      "audio/mp4",
      "audio/ogg",
      "audio/wav",
      "audio/aac",
      "", // Empty string = browser default
    ];

    for (const type of types) {
      if (type === "" || MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return ""; // Use browser default if none supported
  };

  // Clean up resources when component unmounts
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  // Start recording
  const startRecording = async (): Promise<void> => {
    if (isRecording) return;

    setIsProcessing(true);

    try {
      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Reset audio chunks
      audioChunksRef.current = [];

      // Get media stream with proper constraints for mobile
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });

      streamRef.current = stream;

      // Get supported MIME type
      const mimeType = getSupportedMimeType();

      // Create media recorder with proper options
      const options: MediaRecorderOptions = {};
      if (mimeType) options.mimeType = mimeType;

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      // Set up event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Create blob with all available chunks
        const audioType = mimeType || "audio/mp4";
        const blob = new Blob(audioChunksRef.current, { type: audioType });
        setAudioBlob(blob);
      };

      // Start recording with a timeslice to ensure chunks are collected
      // This helps on iOS where ondataavailable might not fire until stop
      mediaRecorder.start(timeslice);
      setIsRecording(true);

      // Start timer for recording duration
      let seconds = 0;
      recordingTimerRef.current = window.setInterval(() => {
        seconds += 1;
        setRecordingDuration(seconds);

        // Auto-stop after maxDuration seconds
        if (seconds >= maxDuration) {
          stopRecording();
        }
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Stop recording
  const stopRecording = (): void => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") return;

    try {
      // Request final chunk of data and stop recording
      mediaRecorderRef.current.requestData();
      mediaRecorderRef.current.stop();

      // Clean up timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }

      setIsRecording(false);
    } catch (error) {
      console.error("Error stopping recording:", error);
      setIsRecording(false);
    }
  };

  // Cancel recording
  const cancelRecording = (): void => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        // Stop the media recorder
        if (mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }

        // Clean up resources
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }

        setIsRecording(false);
        setAudioBlob(null);
        setRecordingDuration(0);
        audioChunksRef.current = [];
      } catch (error) {
        console.error("Error canceling recording:", error);
      }
    }
  };

  // Reset recording state without stopping
  const resetRecording = (): void => {
    setAudioBlob(null);
    setRecordingDuration(0);
    audioChunksRef.current = [];
  };

  // Return state and controls
  return [
    { isRecording, recordingDuration, audioBlob, isProcessing },
    { startRecording, stopRecording, cancelRecording, resetRecording },
  ];
}
